import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Camera, ZoomIn, Check, X } from "lucide-react";
import useUserId from "@/hooks/useUserId";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const VIEW = 260;

export default function AvatarUploader({ size = 58, name = "A", onChanged, testid = "avatar-uploader" }) {
  const userId = useUserId();
  const inputRef = useRef(null);
  const imgRef = useRef(null);
  const drag = useRef(null);
  const [url, setUrl] = useState(localStorage.getItem("aya_avatar_url") || null);
  const [busy, setBusy] = useState(false);
  const [crop, setCrop] = useState(null); // { src, zoom, x, y, w, h }

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/profile/${userId}`)
      .then((r) => {
        const u = r.data?.profile?.avatar_url || null;
        if (u) { setUrl(u); localStorage.setItem("aya_avatar_url", u); }
      })
      .catch(() => {});
  }, [userId]);

  const pick = () => inputRef.current?.click();
  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return toast.error("Format accepté : JPG, PNG ou WebP.");
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => setCrop({ src, zoom: 1, x: 0, y: 0, w: img.width, h: img.height });
    img.src = src;
  };

  const baseScale = crop ? Math.max(VIEW / crop.w, VIEW / crop.h) : 1;
  const scale = crop ? baseScale * crop.zoom : 1;
  const clamp = (v, extent) => Math.max(-extent, Math.min(extent, v));
  const setPos = (x, y) => setCrop((c) => {
    const s = Math.max(VIEW / c.w, VIEW / c.h) * c.zoom;
    return { ...c, x: clamp(x, Math.max(0, (c.w * s - VIEW) / 2)), y: clamp(y, Math.max(0, (c.h * s - VIEW) / 2)) };
  });

  const onPointerDown = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, ox: crop.x, oy: crop.y }; e.target.setPointerCapture?.(e.pointerId); };
  const onPointerMove = (e) => { if (drag.current) setPos(drag.current.ox + (e.clientX - drag.current.sx), drag.current.oy + (e.clientY - drag.current.sy)); };
  const onPointerUp = () => { drag.current = null; };
  const setZoom = (z) => setCrop((c) => {
    const next = { ...c, zoom: z };
    const s = Math.max(VIEW / c.w, VIEW / c.h) * z;
    return { ...next, x: clamp(c.x, Math.max(0, (c.w * s - VIEW) / 2)), y: clamp(c.y, Math.max(0, (c.h * s - VIEW) / 2)) };
  });

  const confirmCrop = async () => {
    if (!userId || !crop) return;
    setBusy(true);
    try {
      const img = imgRef.current;
      const out = document.createElement("canvas");
      out.width = 256; out.height = 256;
      const ctx = out.getContext("2d");
      const r = 256 / VIEW;
      const dw = crop.w * scale * r;
      const dh = crop.h * scale * r;
      ctx.drawImage(img, (256 - dw) / 2 + crop.x * r, (256 - dh) / 2 + crop.y * r, dw, dh);
      const dataUrl = out.toDataURL("image/jpeg", 0.88);
      const resp = await axios.post(`${API}/profile/${userId}/photo`, { photo: dataUrl });
      const u = resp.data?.avatar_url;
      if (u) {
        setUrl(u); localStorage.setItem("aya_avatar_url", u);
        toast.success("Photo de profil mise à jour ✓");
        onChanged?.(u);
      }
      URL.revokeObjectURL(crop.src);
      setCrop(null);
    } catch {
      toast.error("Impossible d'enregistrer la photo. Réessayez.");
    } finally { setBusy(false); }
  };

  const cancelCrop = () => { if (crop) URL.revokeObjectURL(crop.src); setCrop(null); };
  const initial = (name || "A").trim().charAt(0).toUpperCase() || "A";

  return (
    <span style={{ position: "relative", display: "inline-block", flexShrink: 0 }}>
      <button onClick={pick} data-testid={testid} aria-label="Changer ma photo de profil" disabled={busy}
        style={{
          width: size, height: size, borderRadius: "50%", border: "2px solid #10B981", cursor: "pointer",
          background: "#0B3B27", color: "#E8F6EC", fontSize: size * 0.38, fontWeight: 900, padding: 0,
          display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
          boxShadow: "0 0 14px rgba(16,185,129,0.35)", opacity: busy ? 0.6 : 1,
        }}>
        {url
          ? <img src={url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${url}` : url} alt="Photo de profil" style={{ width: "100%", height: "100%", objectFit: "cover" }} data-testid={`${testid}-img`} />
          : initial}
      </button>
      <span onClick={pick} data-testid={`${testid}-camera`} role="button" aria-label="Ajouter une photo"
        style={{
          position: "absolute", right: -3, bottom: -3, width: Math.max(20, size * 0.36), height: Math.max(20, size * 0.36),
          borderRadius: "50%", background: "linear-gradient(135deg,#10B981,#0E7A4F)", display: "flex",
          alignItems: "center", justifyContent: "center", cursor: "pointer", border: "2px solid #04150D",
        }}>
        <Camera size={Math.max(10, size * 0.18)} color="#fff" />
      </span>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
        onChange={onFile} data-testid={`${testid}-input`} />

      {crop && (
        <div data-testid={`${testid}-crop-modal`} style={{
          position: "fixed", inset: 0, zIndex: 120, background: "rgba(0,0,0,0.8)",
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, padding: 16,
        }}>
          <div style={{ color: "#fff", fontSize: 14, fontWeight: 800 }}>Recadrer ma photo</div>
          <div
            onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
            data-testid={`${testid}-crop-area`}
            style={{ position: "relative", width: VIEW, height: VIEW, overflow: "hidden", borderRadius: 16, touchAction: "none", cursor: "grab", background: "#04150D" }}>
            <img ref={imgRef} src={crop.src} alt="" draggable={false}
              style={{
                position: "absolute", left: "50%", top: "50%", width: crop.w, height: crop.h, maxWidth: "none",
                transform: `translate(-50%, -50%) translate(${crop.x}px, ${crop.y}px) scale(${scale})`,
                userSelect: "none", pointerEvents: "none",
              }} />
            <div style={{ position: "absolute", inset: 0, borderRadius: "50%", boxShadow: "0 0 0 999px rgba(0,0,0,0.55)", border: "2px solid #10B981", pointerEvents: "none" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, width: VIEW }}>
            <ZoomIn size={16} color="#9DB4A6" />
            <input type="range" min="1" max="3" step="0.01" value={crop.zoom} data-testid={`${testid}-zoom`}
              onChange={(e) => setZoom(Number(e.target.value))} style={{ flex: 1, accentColor: "#10B981" }} />
          </div>
          <div style={{ display: "flex", gap: 10, width: VIEW }}>
            <button onClick={cancelCrop} data-testid={`${testid}-crop-cancel`}
              style={{ flex: 1, height: 40, borderRadius: 999, border: "1px solid rgba(255,255,255,0.35)", background: "transparent", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <X size={14} /> Annuler
            </button>
            <button onClick={confirmCrop} disabled={busy} data-testid={`${testid}-crop-save`}
              style={{ flex: 1, height: 40, borderRadius: 999, border: "none", background: "linear-gradient(135deg,#10B981,#0E7A4F)", color: "#fff", fontSize: 12.5, fontWeight: 800, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: busy ? 0.6 : 1 }}>
              <Check size={14} /> {busy ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      )}
    </span>
  );
}
